import { css, keyframes } from 'styled-components';

/**
 * Motion for the loading mark. Generated by `loader-playground/index.html` on branch
 * `rout-3223-logo-animation-playground` @ bc940b93d (Export panel -> "Copy CSS + SVG").
 *
 * DO NOT hand-edit the keyframe bodies. To change the motion, check out that branch, run
 * `npx vite loader-playground --port 4321`, tune, and re-paste the four blocks wholesale.
 *
 * Generated from:
 *   hold 0.25s · explode 1s · dance 1s · rejoin 1s     cycle 3.25s, loop 6.5s
 *   spread 80u · scale while apart 120% · centre inset 0.5u
 *   travel to next slot 90° · hexagon turn in dance 90° · no whole-mark rotation
 *   easing cubic-bezier(.5,0,.1,1) on all three phases
 *
 * Two things about the output that look like mistakes but aren't:
 * - The doubled 19.231% / 34.615% / 69.231% / 84.615% stops come from zero-weight pause
 *   phases. CSS last-wins, so the second of each pair supplies the easing. Deduping them
 *   would make the next re-tune an unreadable diff.
 * - `animation-delay` was dropped because stagger is 0. It has to come back if stagger
 *   ever goes non-zero.
 *
 * Rendered size is not baked in: every transform is viewBox-relative, so changing the
 * SVG's width/height needs no regeneration.
 */

/**
 * One full loop of the mark: two explode/rejoin cycles. Must match the playground's
 * `dur` setting, since the keyframe percentages below are relative to it.
 */
export const MARK_LOOP_MS = 6500;

/** A single explode/rejoin cycle. The mark rejoins twice per loop. */
export const MARK_CYCLE_MS = MARK_LOOP_MS / 2;

/**
 * Where the explode begins within a cycle, as a fraction of it. Derived from the
 * playground's phase weights — hold 5, explode 20, dance 20, rejoin 20, of 65 total — so
 * the opening-hold offset below follows automatically if those weights are re-tuned.
 */
const PHASE_TOTAL = 65;
export const MARK_PHASES = {
  explodeStart: 5 / PHASE_TOTAL,
};

/**
 * How far into the timeline the animation starts, skipping the opening hold. index.html
 * shows the mark motionless while the bundle loads, so replaying that rest beat on mount
 * reads as more waiting rather than as a beat of its own. Landing on the hold's final
 * frame is safe because it's identical to the 0% pose — the mark is exactly where the
 * boot copy left it, and the explode begins on the next frame.
 *
 * Only the opening hold is skipped; the ones between later cycles are untouched.
 *
 * Rounded because 5/65 isn't exact in binary floating point, and loaderTimeline.ts offsets
 * the shared clock by this same value.
 */
export const MARK_START_OFFSET_MS = Math.round(MARK_CYCLE_MS * MARK_PHASES.explodeStart);

const petal0 = keyframes`
  0% { transform: translate(145.254px,145.254px) rotate(0deg) scale(1) translate(-60.9px,-60.9px); }
  3.846% { transform: translate(145.254px,145.254px) rotate(0deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  19.231% { transform: translate(88.685px,88.685px) rotate(0deg) scale(1.2) translate(-60.9px,-60.9px); }
  19.231% { transform: translate(88.685px,88.685px) rotate(0deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  34.615% { transform: translate(266.79px,93.21px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); }
  34.615% { transform: translate(266.79px,93.21px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  50% { transform: translate(214.746px,145.254px) rotate(90deg) scale(1) translate(-60.9px,-60.9px); }
  53.846% { transform: translate(214.746px,145.254px) rotate(90deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  69.231% { transform: translate(271.315px,88.685px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); }
  69.231% { transform: translate(271.315px,88.685px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  84.615% { transform: translate(266.79px,266.79px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); }
  84.615% { transform: translate(266.79px,266.79px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  100% { transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); }
`;

const petal1 = keyframes`
  0% { transform: translate(214.746px,145.254px) rotate(90deg) scale(1) translate(-60.9px,-60.9px); }
  3.846% { transform: translate(214.746px,145.254px) rotate(90deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  19.231% { transform: translate(271.315px,88.685px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); }
  19.231% { transform: translate(271.315px,88.685px) rotate(90deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  34.615% { transform: translate(266.79px,266.79px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); }
  34.615% { transform: translate(266.79px,266.79px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  50% { transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); }
  53.846% { transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  69.231% { transform: translate(271.315px,271.315px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); }
  69.231% { transform: translate(271.315px,271.315px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  84.615% { transform: translate(93.21px,266.79px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); }
  84.615% { transform: translate(93.21px,266.79px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  100% { transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); }
`;

const petal2 = keyframes`
  0% { transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); }
  3.846% { transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  19.231% { transform: translate(271.315px,271.315px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); }
  19.231% { transform: translate(271.315px,271.315px) rotate(180deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  34.615% { transform: translate(93.21px,266.79px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); }
  34.615% { transform: translate(93.21px,266.79px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  50% { transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); }
  53.846% { transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  69.231% { transform: translate(88.685px,271.315px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); }
  69.231% { transform: translate(88.685px,271.315px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  84.615% { transform: translate(93.21px,93.21px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); }
  84.615% { transform: translate(93.21px,93.21px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  100% { transform: translate(145.254px,145.254px) rotate(360deg) scale(1) translate(-60.9px,-60.9px); }
`;

const petal3 = keyframes`
  0% { transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); }
  3.846% { transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  19.231% { transform: translate(88.685px,271.315px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); }
  19.231% { transform: translate(88.685px,271.315px) rotate(270deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  34.615% { transform: translate(93.21px,93.21px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); }
  34.615% { transform: translate(93.21px,93.21px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  50% { transform: translate(145.254px,145.254px) rotate(360deg) scale(1) translate(-60.9px,-60.9px); }
  53.846% { transform: translate(145.254px,145.254px) rotate(360deg) scale(1) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  69.231% { transform: translate(88.685px,88.685px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); }
  69.231% { transform: translate(88.685px,88.685px) rotate(360deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  84.615% { transform: translate(266.79px,93.21px) rotate(450deg) scale(1.2) translate(-60.9px,-60.9px); }
  84.615% { transform: translate(266.79px,93.21px) rotate(450deg) scale(1.2) translate(-60.9px,-60.9px); animation-timing-function: cubic-bezier(.5,0,.1,1); }
  100% { transform: translate(214.746px,145.254px) rotate(450deg) scale(1) translate(-60.9px,-60.9px); }
`;

// Reduced motion keeps a loading affordance without the vestibular trigger: the petals
// hold their assembled positions and only opacity moves.
const markBreathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

export const loaderMotion = css`
  .k {
    /* Pairs with the px translates below: without view-box they'd resolve against the
       element's own bounding box and the mark would land somewhere else entirely. */
    transform-box: view-box;
    transform-origin: 0 0;
    animation-duration: ${MARK_LOOP_MS}ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    /* Set per instance from the shared clock (see loaderTimeline.ts) so a remount resumes
       mid-loop instead of bursting from assembled again. The fallback is the first
       instance's value: the opening hold skipped, poised on the explode. If the generated
       per-petal stagger delays ever come back (see the header), this has to be added to
       each of them rather than replacing them. */
    animation-delay: var(--loader-mark-delay, -${MARK_START_OFFSET_MS}ms);
  }

  /* Duplicates each petal's 0% keyframe, so killing the animation leaves the mark
     assembled rather than collapsing all four onto the raw path coords. index.html's boot
     mark copies these four values; re-tuning the poses means updating it too. */
  .k0 { animation-name: ${petal0}; transform: translate(145.254px,145.254px) rotate(0deg) scale(1) translate(-60.9px,-60.9px); }
  .k1 { animation-name: ${petal1}; transform: translate(214.746px,145.254px) rotate(90deg) scale(1) translate(-60.9px,-60.9px); }
  .k2 { animation-name: ${petal2}; transform: translate(214.746px,214.746px) rotate(180deg) scale(1) translate(-60.9px,-60.9px); }
  .k3 { animation-name: ${petal3}; transform: translate(145.254px,214.746px) rotate(270deg) scale(1) translate(-60.9px,-60.9px); }

  @media (prefers-reduced-motion: reduce) {
    .k { animation: none; }
    animation: ${markBreathe} 2s ease-in-out infinite;
  }
`;
